import { useCallback, useEffect, useMemo, useState } from 'react'
import { formatAddress } from '../../../utils/formatAddress'
import { Props } from '../DrawerContentsSingle'
import { ENTITY_TYPES } from '@sales-center/common'
import { usePropertyDetailsEnabled } from '@user/components/DetailPages/sections/EntityName/MeasureAI/hooks/usePropertyDetailsEnabled'
import { useFormPacketsEnabled } from '@user/store/tenantFeatures/hooks/useFormPacketsEnabled'

export type ErrorModalState = {
  open: boolean
  title: string
  message: string
  showAddServiceButton?: boolean
  showPestPacActions?: boolean
  onRetry?: () => void
}

export function useData(props: Props) {
  const {
    entityDetails,
    serviceLocation: { contact },
    variant,
  } = props

  const formPacketEnabled = useFormPacketsEnabled()

  const [cancelPopupOpen, setCancelPopupOpen] = useState<boolean>(false)
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    open: false,
    title: '',
    message: '',
  })
  const [svcState, setSvcState] = useState<{
    svcOpen: boolean
    isAddingService: boolean
    openedFromAgreement: boolean
  }>(() => ({
    svcOpen: false,
    isAddingService: false,
    openedFromAgreement: false,
  }))
  const [createLocationDrawerOpen, setCreateLocationDrawerOpen] =
    useState<boolean>(false)

  const addressDisplay = useMemo(() => {
    if (entityDetails.entityType === ENTITY_TYPES.DraftLead) {
      return entityDetails
        ? formatAddress(
            entityDetails.street1,
            entityDetails.street2,
            entityDetails.city,
            entityDetails.stateAbbreviation,
            entityDetails.postalCode,
          )
        : ''
    } else {
      return contact
        ? formatAddress(
            contact.street1,
            contact.street2,
            contact.city,
            contact.stateAbbreviation,
            contact.postalCode,
          )
        : ''
    }
  }, [entityDetails, contact])

  const [tabIndex, setTabIndex] = useState(0)

  useEffect(() => {
    setTabIndex(0)
  }, [])

  const entityType: ENTITY_TYPES = useMemo(() => {
    switch (variant.toLowerCase()) {
      case 'lead':
        return ENTITY_TYPES.Lead
      case 'opportunity':
        return ENTITY_TYPES.Opportunity
      case 'draftlead':
        return ENTITY_TYPES.DraftLead
      default:
        return ENTITY_TYPES.Lead
    }
  }, [variant])

  const measureAIEnabled = usePropertyDetailsEnabled()

  const hasValidAddress = useMemo(
    () =>
      entityDetails.entityType === ENTITY_TYPES.DraftLead
        ? entityDetails &&
          entityDetails.street1 &&
          entityDetails.city &&
          entityDetails.stateAbbreviation &&
          entityDetails.postalCode
          ? true
          : false
        : contact &&
            contact.street1 &&
            contact.city &&
            contact.stateAbbreviation &&
            contact.postalCode
          ? true
          : false,
    [entityDetails, contact],
  )

  const nameAndCompanyDisplay = useMemo(() => {
    if (!props?.serviceLocation) {
      return ''
    }
    const companyName = props?.serviceLocation.company
    const name = `${props?.serviceLocation.firstName} ${props?.serviceLocation.lastName}`
    const items = [name, companyName].filter((x) => x && x.trim().length > 0)
    if (items.length > 1) {
      return items.join(' / ')
    } else {
      return items[0] || ''
    }
  }, [props?.serviceLocation])

  const onOpenSvc = useCallback(() => {
    setSvcState({
      svcOpen: true,
      isAddingService: false,
      openedFromAgreement: false,
    })
  }, [])

  const onOpenSvcFromAgreement = useCallback(() => {
    setSvcState({
      svcOpen: true,
      isAddingService: false,
      openedFromAgreement: true,
    })
  }, [])

  const onCloseSvc = useCallback(() => {
    setSvcState({
      svcOpen: false,
      isAddingService: false,
      openedFromAgreement: !!svcState.openedFromAgreement,
    })
  }, [svcState.openedFromAgreement])

  const handleCancel = useCallback(() => {
    setDrawerOpen(false)
    setCancelPopupOpen(false)
  }, [])

  const hideCancelDialog = useCallback(() => {
    setCancelPopupOpen(false)
  }, [])

  const openCreateServiceDrawer = useCallback(() => {
    setCreateLocationDrawerOpen(true)
  }, [])

  const closeCreateServiceDrawer = useCallback(() => {
    setCreateLocationDrawerOpen(false)
  }, [])

  const deleteServiceWarningMessage =
    entityType === ENTITY_TYPES.Lead
      ? ''
      : 'Opportunities require at least one product or service'

  const minimumServicesCount = entityType === ENTITY_TYPES.Lead ? 0 : 1

  const closeCreateDrawer = useCallback(() => {
    if (formPacketEnabled) {
      setCancelPopupOpen(true)
    } else {
      handleCancel()
    }
  }, [formPacketEnabled, handleCancel])

  return {
    addressDisplay,
    entityType,
    hasValidAddress,
    measureAIEnabled,
    tabIndex,
    setTabIndex,
    nameAndCompanyDisplay,
    errorModal,
    setErrorModal,
    drawerOpen,
    setDrawerOpen,
    formPacketEnabled,
    onOpenSvc,
    onOpenSvcFromAgreement,
    ...svcState,
    onCloseSvc,
    cancelPopupOpen,
    handleCancel,
    hideCancelDialog,
    openCreateServiceDrawer,
    closeCreateServiceDrawer,
    createLocationDrawerOpen,
    deleteServiceWarningMessage,
    minimumServicesCount,
    closeCreateDrawer,
  }
}

export type UseDataReturn = ReturnType<typeof useData>
